import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import icons from '../config/icons.config'
import { useModal } from '../contexts/ModalContext'
import { useSignIn } from '../hooks/auth/useSignIn'
import { ScreenPath } from '../navigation'
import { logError } from '../util/errorLogger'
import styles from '../ui/auth/AuthForm.module.css'
import AppIcon from '../ui/icons/AppIcon'
import SimplePageLayout from '../ui/layout/SimplePageLayout'
import Button from '../ui/primitives/Button'
import TextField from '../ui/primitives/TextField'

export interface LoginForm {
  email: string
  password: string
}

export default function LoginScreen() {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LoginForm>()
  const navigate = useNavigate()
  const { showSnackbar } = useModal()
  const location = useLocation()
  const from = location.state?.from
  const { mutateAsync: signIn, isPending } = useSignIn()
  const [showPassword, setShowPassword] = useState(false)

  async function onSubmit(data: LoginForm) {
    try {
      await signIn({ email: data.email, password: data.password })
      navigate(from ?? ScreenPath.home, { replace: true })
    } catch (e) {
      void logError(e, { screen: 'Login' })
      showSnackbar(
        'login-error',
        'Sorry! There was a problem signing in. It could be a wrong username/password',
        'error',
      )
    }
  }

  return (
    <SimplePageLayout title="Log in">
      <div className={styles.card}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <TextField
            label="Email"
            type="email"
            error={errors.email?.message}
            {...register('email', { required: 'Email is required' })}
          />
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            error={errors.password?.message}
            endAdornment={
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label="Toggle password visibility"
              >
                <AppIcon icon={showPassword ? icons.eyeOff : icons.eye} size={18} />
              </button>
            }
            {...register('password', { required: 'Password is required' })}
          />
          <div className={styles.actions}>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Signing in…' : 'Log in'}
            </Button>
            <p className={styles.linkRow}>
              Don&apos;t have an account?{' '}
              <button
                type="button"
                className={styles.linkButton}
                onClick={() =>
                  navigate(ScreenPath.signup, {
                    state: { from: location.state?.from },
                    replace: true,
                  })
                }
              >
                Sign up
              </button>
            </p>
            <p className={styles.linkRow}>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => navigate(ScreenPath.forgotPassword)}
              >
                Forgot password?
              </button>
            </p>
          </div>
        </form>
      </div>
    </SimplePageLayout>
  )
}
